import React from "react";
import {
  Html,
  Head,
  Body,
  Container,
  Section,
  Heading,
  Text,
  Button,
  Preview,
  Img,
  Hr,
  Row,
  Column,
} from "@react-email/components";

const EmailTemplate = ({ layout, globalSettings }) => {
  const main = {
    backgroundColor: globalSettings.pageColor,
    fontFamily: globalSettings.fontFamily,
  };
  const container = {
    backgroundColor: globalSettings.containerColor,
    margin: "0 auto",
    padding: "0px",
    borderRadius: "12px",
    maxWidth: `${globalSettings.containerWidth || 600}px`,
    border: `1px solid ${globalSettings.borderColor}`,
    overflow: "hidden",
  };

  const styles = {
    heading: { lineHeight: "1.2", margin: 0 },
    text: { lineHeight: "1.5", whiteSpace: "pre-wrap", margin: 0 },
    image: {
      width: "100%",
      objectFit: "cover",
      display: "block",
    },
    button: {
      borderRadius: "6px",
      textDecoration: "none",
      display: "inline-block",
      padding: "12px 24px",
    },
    divider: {
      borderTop: `1px solid ${globalSettings.borderColor}`,
      width: "100%",
    },
  };

  const renderTextContent = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i, arr) => (
      <span key={i}>
        {line}
        {i !== arr.length - 1 && <br />}
      </span>
    ));
  };

  const renderBlock = (block, isColumn = false) => {
    // Handle numerical gradual alignment
    let alignmentStyle = { textAlign: 'center' };
    let gradualPadding = {};
    
    if (typeof block.align === 'number') {
      if (block.align > 0) gradualPadding = { paddingLeft: `${block.align}%` };
      else if (block.align < 0) gradualPadding = { paddingRight: `${Math.abs(block.align)}%` };
    } else {
      alignmentStyle.textAlign = block.align || (["image", "button", "social"].includes(block.type) ? "center" : "left");
    }

    const wrapperStyle = {
      width: "100%",
      ...alignmentStyle,
      ...gradualPadding,
      paddingTop: `${block.marginTop || 0}px`,
      paddingBottom: `${block.marginBottom || 0}px`,
    };

    const innerPaddingStyle = {
      paddingTop: `${block.innerPaddingTop || 0}px`,
      paddingBottom: `${block.innerPaddingBottom || 0}px`,
      paddingLeft: `${block.innerPaddingLeft || 0}px`,
      paddingRight: `${block.innerPaddingRight || 0}px`,
    };

    switch (block.type) {
      case "heading":
        return (
          <div style={wrapperStyle}>
            <Heading
              style={{
                ...styles.heading,
                color: block.color || globalSettings.headingColor,
                fontSize: block.fontSize ? `${block.fontSize}px` : "24px",
                fontWeight: block.isBold !== false ? "bold" : "normal",
                fontStyle: block.isItalic ? "italic" : "normal",
                textDecoration: block.isUnderline ? "underline" : "none",
                display: 'inline-block',
                ...innerPaddingStyle,
              }}
            >
              {renderTextContent(block.text)}
            </Heading>
          </div>
        );
      case "text":
        return (
          <div style={wrapperStyle}>
            <Text
              style={{
                ...styles.text,
                color: block.color || globalSettings.textColor,
                fontSize: block.fontSize ? `${block.fontSize}px` : "15px",
                fontWeight: block.isBold ? "bold" : "normal",
                fontStyle: block.isItalic ? "italic" : "normal",
                textDecoration: block.isUnderline ? "underline" : "none",
                display: 'inline-block',
                ...innerPaddingStyle,
              }}
            >
              {renderTextContent(block.text)}
            </Text>
          </div>
        );
      case "image":
        const widthVal =
          block.width && parseInt(block.width) > 0
            ? parseInt(block.width)
            : undefined;
        return (
          <div style={wrapperStyle}>
            <Img
              src={
                block.src ||
                "https://via.placeholder.com/600x250/2a0a4a/ffffff?text=Image"
              }
              width={widthVal}
              alt="Content"
              style={{
                ...styles.image,
                width: widthVal ? `${widthVal}px` : "100%",
                maxWidth: "100%",
                borderRadius: block.isHero && !isColumn ? "0" : "8px",
                display: 'inline-block',
                ...innerPaddingStyle,
              }}
            />
          </div>
        );
      case "button":
        const gradStart = block.gradientStart || "#a855f7";
        const gradEnd = block.gradientEnd || "#4f46e5";
        const btnBackground = block.useGradient
          ? `linear-gradient(to right, ${gradStart}, ${gradEnd})`
          : block.bgColor || "#a855f7";
        return (
          <div style={wrapperStyle}>
            <Button
              style={{
                ...styles.button,
                background: btnBackground,
                backgroundColor: block.bgColor || "#a855f7",
                color: block.color || "#ffffff",
                fontSize: block.fontSize ? `${block.fontSize}px` : "16px",
                fontWeight: block.isBold !== false ? "bold" : "normal",
                ...innerPaddingStyle,
              }}
              href={block.url || "https://legacyx.uk"}
            >
              {block.text}
            </Button>
          </div>
        );
      case "divider":
        return (
          <Hr
            style={{
              ...styles.divider,
              marginTop: `${block.paddingTop || 16}px`,
              marginBottom: `${block.paddingBottom || 16}px`,
            }}
          />
        );
      case "social":
        return (
          <div style={wrapperStyle}>
            <div style={{ display: "inline-block", ...innerPaddingStyle }}>
              {block.socials &&
                block.socials.map((social) => (
                  <a
                    key={social.id}
                    href={social.url || "#"}
                    style={{ display: "inline-block", margin: "0 8px" }}
                  >
                    <Img
                      src={social.iconUrl}
                      width={block.iconSize || 24}
                      height={block.iconSize || 24}
                      alt={social.platform}
                    />
                  </a>
                ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Html>
      <Head />
      <Preview>{globalSettings.previewText || "Update"}</Preview>
      <Body style={main}>
        <Container style={container}>
          {layout.map((row) => (
            <Section key={row.id}>
              <Row>
                {row.columns.map((col) => (
                  <Column
                    key={col.id}
                    style={{
                      width: `${100 / row.columns.length}%`,
                      verticalAlign: "top",
                    }}
                  >
                    {col.blocks.map((block) => (
                      <div key={block.id}>
                        {renderBlock(block, row.columns.length > 1)}
                      </div>
                    ))}
                  </Column>
                ))}
              </Row>
            </Section>
          ))}
        </Container>
      </Body>
    </Html>
  );
};

export default EmailTemplate;
